const { fiscalYearForDate } = require('./budgetFiscal');

const MAX_EXPENSE_OCCURRENCES = 1000;
const MAX_EXPENSE_AMOUNT_CENTS = 99999999999999;
const EXPENSE_SCOPES = ['this', 'this_and_following', 'series'];
const DAY_MS = 24 * 60 * 60 * 1000;

function parseExpenseDate(value) {
  const match = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error('Enter a valid expense date.');
  const [year, month, day] = match.slice(1).map(Number);
  const stamp = Date.UTC(year, month - 1, day);
  const date = new Date(stamp);
  if (year < 1900 || year > 2199 || date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new Error('Enter a valid expense date.');
  }
  return { year, month, day, stamp };
}

function formatDate(year, month, day) {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function expenseAmountCents(amount) {
  if ((typeof amount !== 'string' && typeof amount !== 'number') ||
      !/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(String(amount).trim())) {
    throw new Error('Enter an amount greater than zero with no more than two decimal places.');
  }
  const [whole, fraction = ''] = String(amount).trim().split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents) || cents <= 0 || cents > MAX_EXPENSE_AMOUNT_CENTS) {
    throw new Error('Enter a valid amount greater than zero.');
  }
  return cents;
}

function fiscalYearEnd(date) {
  parseExpenseDate(date);
  const year = Number(fiscalYearForDate(date).slice(5));
  return `${year}-03-31`;
}

function normalizeExpenseSchedule(input = {}) {
  parseExpenseDate(input.date);
  const amountCents = input.amount === undefined ? input.amountCents : expenseAmountCents(input.amount);
  if (!Number.isSafeInteger(amountCents) || amountCents <= 0 || amountCents > MAX_EXPENSE_AMOUNT_CENTS) {
    throw new Error('Enter a valid amount greater than zero.');
  }
  if (input.amount !== undefined && input.amountCents !== undefined && input.amountCents !== amountCents) {
    throw new Error('The expense amount is inconsistent.');
  }
  if (input.recurring !== undefined && typeof input.recurring !== 'boolean') {
    throw new Error('Choose whether the expense repeats.');
  }
  const recurring = input.recurring === true;
  if (!recurring) {
    // Ignore stale hidden recurrence controls when the user switches to one-off.
    return { date: input.date, amountCents, recurring: false, frequency: null, interval: 1, endDate: input.date };
  }
  const frequency = input.frequency === undefined ? 'monthly' : input.frequency;
  if (!['weekly', 'monthly', 'yearly'].includes(frequency)) {
    throw new Error('Choose weekly, monthly or yearly recurrence.');
  }
  const rawInterval = input.interval === undefined ? 1 : input.interval;
  const interval = typeof rawInterval === 'string' && /^\d+$/.test(rawInterval) ? Number(rawInterval) : rawInterval;
  if (!Number.isSafeInteger(interval) || interval < 1) {
    throw new Error('Enter a repeat interval of at least one.');
  }
  const anchorDate = input.anchorDate || input.date;
  parseExpenseDate(anchorDate);
  if (anchorDate > input.date) throw new Error('The repeat anchor must be on or before the start date.');
  parseExpenseDate(input.endDate);
  if (input.endDate < input.date) throw new Error('The end date must be on or after the start date.');
  return { date: input.date, amountCents, recurring: true, frequency, interval, endDate: input.endDate, anchorDate };
}

function occurrenceDate(schedule, anchor, index) {
  if (schedule.frequency === 'weekly') {
    const stamp = anchor.stamp + index * schedule.interval * 7 * DAY_MS;
    if (!Number.isSafeInteger(stamp) || stamp > Date.UTC(2199, 11, 31)) return null;
    const date = new Date(stamp);
    return formatDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }
  const months = schedule.interval * (schedule.frequency === 'yearly' ? 12 : 1);
  const absoluteMonth = anchor.year * 12 + anchor.month - 1 + index * months;
  const year = Math.floor(absoluteMonth / 12);
  if (year > 2199) return null;
  const month = absoluteMonth % 12 + 1;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  // Always use the original day: Jan 31 -> Feb 28 -> Mar 31, never Mar 28.
  return formatDate(year, month, Math.min(anchor.day, lastDay));
}

function generateExpenseOccurrences(input) {
  const schedule = normalizeExpenseSchedule(input);
  const anchor = parseExpenseDate(schedule.anchorDate || schedule.date);
  const occurrences = [];
  for (let anchorIndex = 0; ; anchorIndex += 1) {
    const date = schedule.recurring ? occurrenceDate(schedule, anchor, anchorIndex) : schedule.date;
    if (!date || date > schedule.endDate) break;
    if (date < schedule.date) continue;
    const occurrenceIndex = occurrences.length;
    if (occurrenceIndex >= MAX_EXPENSE_OCCURRENCES) {
      throw new Error(`Limit the schedule to ${MAX_EXPENSE_OCCURRENCES} expenses or fewer.`);
    }
    if (!Number.isSafeInteger(schedule.amountCents * (occurrenceIndex + 1))) {
      throw new Error('The total expense amount is too large.');
    }
    occurrences.push({ occurrenceIndex, date, amountCents: schedule.amountCents, fiscalYear: fiscalYearForDate(date) });
    if (!schedule.recurring) break;
  }
  return occurrences;
}

function summarizeExpenseFiscalYears(occurrences) {
  const years = new Map();
  for (const occurrence of occurrences) {
    parseExpenseDate(occurrence.date);
    const fiscalYear = fiscalYearForDate(occurrence.date);
    if (!Number.isSafeInteger(occurrence.amountCents) || occurrence.amountCents <= 0) {
      throw new Error('Invalid expense amount.');
    }
    const summary = years.get(fiscalYear) || { fiscalYear, amountCents: 0, count: 0 };
    summary.amountCents += occurrence.amountCents;
    summary.count += 1;
    if (!Number.isSafeInteger(summary.amountCents)) throw new Error('The total expense amount is too large.');
    years.set(fiscalYear, summary);
  }
  return [...years.values()].sort((a, b) => a.fiscalYear.localeCompare(b.fiscalYear));
}

function selectExpenseOccurrences(occurrences, selectedId, scope) {
  if (!EXPENSE_SCOPES.includes(scope)) throw new Error('Choose which expenses to change.');
  if (selectedId === undefined || selectedId === null || String(selectedId) === '') {
    throw new Error('Choose an expense to change.');
  }
  const matching = occurrences.filter(row => row.id !== undefined && row.id !== null && String(row.id) === String(selectedId));
  if (matching.length !== 1) throw new Error('The selected expense is no longer available.');
  const selected = matching[0];
  if (scope === 'this' || selected.seriesId === undefined || selected.seriesId === null) return [selected];
  const sameSeries = occurrences.filter(row => row.seriesId !== undefined && row.seriesId !== null && String(row.seriesId) === String(selected.seriesId));
  const indexes = new Set();
  for (const row of sameSeries) {
    if (!Number.isSafeInteger(row.occurrenceIndex) || row.occurrenceIndex < 0 || indexes.has(row.occurrenceIndex)) {
      throw new Error('The expense series has an invalid occurrence order.');
    }
    indexes.add(row.occurrenceIndex);
  }
  return sameSeries.filter(row => scope === 'series' || row.occurrenceIndex >= selected.occurrenceIndex)
    .sort((a, b) => a.occurrenceIndex - b.occurrenceIndex);
}

// Plan a replacement before any persistence. The caller owns IDs and the new
// series header; retained records are returned unchanged, including exceptions.
function replaceExpenseOccurrences(occurrences, selectedId, scope, replacementSchedule) {
  const removed = selectExpenseOccurrences(occurrences, selectedId, scope);
  const removeSet = new Set(removed);
  const kept = occurrences.filter(row => !removeSet.has(row));
  const replacement = replacementSchedule ? generateExpenseOccurrences(replacementSchedule) : [];
  if (scope === 'this' && replacement.length > 1) {
    throw new Error('Editing this expense can replace only one expense.');
  }
  if (scope === 'this' && replacement.length && Number.isSafeInteger(removed[0].occurrenceIndex)) {
    replacement[0].occurrenceIndex = removed[0].occurrenceIndex;
  }
  return { kept, removed, replacement };
}

module.exports = {
  MAX_EXPENSE_OCCURRENCES, MAX_EXPENSE_AMOUNT_CENTS, EXPENSE_SCOPES, expenseAmountCents, fiscalYearEnd,
  normalizeExpenseSchedule, generateExpenseOccurrences, summarizeExpenseFiscalYears,
  selectExpenseOccurrences, replaceExpenseOccurrences,
};
